import * as React from 'react'
import { Routes, Route } from 'react-router-dom'

import ScopedCssBaseline from '@mui/material/ScopedCssBaseline'
import { ThemeProvider, createTheme } from '@mui/material/styles'

import { useBootstrapStore } from './stores/useBootstrapStore'
import LoadingScreen from './components/LoadingScreen'
import ErrorScreen from './components/ErrorScreen'
import useBrowserTheme from './hooks/useBrowserTheme'
import useTimezoneSync from './hooks/useTimezoneSync'
import getThemeOptions from './theme'
import { UserContext } from './UserContext'
import Home from './home'
import Apps from './home/Apps'
import Imports from './home/Imports'
import Settings from './home/Settings'
import Subscription from './home/Subscription'
import Upgrade from './home/Upgrade'
import API from './home/API'
import Evernote from './components/Imports/Evernote'
import Snackbar from './components/Snackbar'
import RecalcToast from './components/RecalcToast'
import UpgradeDialog from './components/Paywall/UpgradeDialog'
import PaywallBoundary from './components/Paywall/PaywallBoundary'
import LastFreeGraphHint from './components/Paywall/LastFreeGraphHint'

import { DialogsProvider } from './DialogsContext'
import ProjectNotes from './components/ProjectNotes'
import GraphToolsBar from './components/GraphToolsBar'
import HideLegacyDom from './components/LeftMenuMigration/HideLegacyDom'
import OnboardingGuide from './components/OnboardingGuide'
import PersonaToast from './components/PersonaToast'
import UpgradeReminder from './components/PersonaToast/UpgradeReminder'
import NarratedBuild from './components/OnboardingGuide/NarratedBuild'
import { getBootNarrationUser } from './stores/onboardingStorage'

// Lazy-load: keeps this large admin-only view out of the public bundle. Safe
// under SSR — bootstrap runs in an effect, so renderToString renders
// LoadingScreen and the lazy chunk only ever resolves client-side.
const AdminPanel = React.lazy(() =>
    import(/* webpackChunkName: "admin-panel" */ './home/AdminPanel')
)

const selectorTheme = state => state.theme
const selectorSetTheme = state => state.setTheme

export default function App(props) {
    // Bootstrap store - single source of truth for initialization
    const bootstrap = useBootstrapStore(state => state.bootstrap)
    const isLoading = useBootstrapStore(state => state.isLoading)
    const bootError = useBootstrapStore(state => state.bootError)
    const isReady = useBootstrapStore(state => state.isReady)
    const user = useBootstrapStore(state => state.user)
    const flags = useBootstrapStore(state => state.flags)
    const getExperimentalEnabled = useBootstrapStore(
        state => state.getExperimentalEnabled
    )

    // The profile's timezone follows the browser's, once per session, for a
    // signed-in user on their own pages (a visitor on a public graph has no
    // profile route to write to).
    useTimezoneSync(
        Boolean(isReady && user && user.isAuthenticated && !user.isPublic)
    )

    // Server passes `initialTheme` (SSR can't read localStorage); the client never
    // receives it and reads the store. Both resolve the same stored preference, so
    // the markup and the first render agree — hydration never patches a className
    // mismatch. Its presence, not `typeof window`, selects the source: a dependency
    // defines `window` on the server. See lib/renderReactApp.js.
    const browserTheme = useBrowserTheme(selectorTheme)
    const setTheme = useBrowserTheme(selectorSetTheme)
    const isDarkMode = (props.initialTheme || browserTheme) === 'dark'

    // Whether the onboarding narration should cover this page load (a new
    // user arriving at their first graph). Set in an effect, not in the
    // initializer: the server rendered this tree without the overlay, so
    // adding it during the hydration pass would mismatch.
    const [bootNarration, setBootNarration] = React.useState(null)
    React.useEffect(() => {
        const bootNarrationUser = getBootNarrationUser()
        setBootNarration(bootNarrationUser)
        // The server-rendered graph page paints a static twin of the
        // narration overlay to cover the pre-hydration gap; NarratedBuild
        // replaces it on mount, but when the narration doesn't apply nothing
        // would mount, so clean it up here.
        if (!bootNarrationUser) {
            document.getElementById('onboarding-boot-narration')?.remove()
        }
    }, [])

    // Run bootstrap once on mount
    React.useEffect(() => {
        bootstrap()
    }, [bootstrap])

    React.useEffect(() => {
        if (window.myEventEmitter) {
            myEventEmitter.on('themeChange', () => {
                const domTheme = document.documentElement.hasAttribute('theme')
                    ? 'dark'
                    : 'light'
                setTheme(domTheme)
            })
        }
    }, [])

    const muiTheme = React.useMemo(
        () => createTheme(getThemeOptions(isDarkMode)),
        [isDarkMode]
    )

    // Show error screen if bootstrap failed
    if (bootError) {
        return (
            <ThemeProvider theme={muiTheme}>
                <ScopedCssBaseline enableColorScheme>
                    <ErrorScreen
                        title="Failed to load application"
                        message={bootError.message || 'Unknown error'}
                    />
                </ScopedCssBaseline>
            </ThemeProvider>
        )
    }

    // Show loading screen until bootstrap completes. When a brand-new user
    // arrives at their first graph, the onboarding narration must cover the
    // boot from the very first paint — OnboardingGuide can't do it (it only
    // mounts in the ready tree), so the overlay renders here too, above the
    // loading screen. Decided synchronously from localStorage; onDone is a
    // no-op because the ready tree's instance takes over before completion.
    if (!isReady) {
        return (
            <ThemeProvider theme={muiTheme}>
                <ScopedCssBaseline enableColorScheme>
                    <LoadingScreen />
                    {bootNarration ? (
                        <NarratedBuild onDone={() => {}} />
                    ) : null}
                </ScopedCssBaseline>
            </ThemeProvider>
        )
    }

    return (
        <ThemeProvider theme={muiTheme}>
            <ScopedCssBaseline enableColorScheme>
                <UserContext.Provider
                    value={{
                        ...user,
                        username: user?.username,
                        // isPublic removed from UserContext: read it via
                        // useContextsStore(selectIsPublicView) instead.
                        // UserContext now holds viewer identity only.
                        flags,
                        flagsLoading: isLoading,
                        experimentalEnabled: getExperimentalEnabled(),
                    }}
                >
                    <DialogsProvider>
                        <Snackbar />
                        <RecalcToast />
                        <PaywallBoundary>
                            <UpgradeDialog />
                            <LastFreeGraphHint />
                        </PaywallBoundary>
                        <ProjectNotes />
                        <GraphToolsBar />
                        <HideLegacyDom />
                        <OnboardingGuide />
                        <PersonaToast prefill={props.profilePrefill} />
                        <UpgradeReminder />
                        <Routes>
                            <Route path="/" element={<Home {...props} />}>
                                <Route
                                    path="apps"
                                    element={<Apps {...props} />}
                                />
                                <Route
                                    path="import"
                                    element={<Imports {...props} />}
                                />
                                <Route
                                    path="settings"
                                    element={<Settings {...props} />}
                                />
                                <Route
                                    path="subscription"
                                    element={<Subscription {...props} />}
                                />
                                <Route
                                    path="upgrade"
                                    element={<Upgrade />}
                                />
                                <Route
                                    path="api-access"
                                    element={<API {...props} />}
                                />
                                <Route
                                    path="admin-panel"
                                    element={
                                        <React.Suspense
                                            fallback={<LoadingScreen />}
                                        >
                                            <AdminPanel {...props} />
                                        </React.Suspense>
                                    }
                                />
                                <Route
                                    path="import/evernote"
                                    element={
                                        <Evernote
                                            evernoteCookie={
                                                props.evernoteCookie
                                            }
                                            evernoteOauthAccessToken={
                                                props.evernoteOauthAccessToken
                                            }
                                            evernoteNotebooks={
                                                props.evernoteNotebooks
                                            }
                                        />
                                    }
                                />
                                <Route
                                    path="import/*"
                                    element={<Imports {...props} />}
                                />
                                <Route path="*" element={null} />
                            </Route>
                        </Routes>
                    </DialogsProvider>
                </UserContext.Provider>
            </ScopedCssBaseline>
        </ThemeProvider>
    )
}
